import { GridPaginationModel, GridRowModel, GridRowsProp, GridSortModel } from '@mui/x-data-grid';
import { createSelector,createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PaginationMode } from 'common/constants/paginationMode';
import { ColumnDefinition } from 'common/types/TypeDefs';
import { FilterFields } from 'common/utils/filterHelpers';

export type PageInfo = {
    id: string,
    name: string,
    tenantName?: string,
    tenantId?: string,
    tenantRequiresLicense: boolean,
    applicationSuiteId?: string,
    applicationSuiteName?: string,
    claimDataTypeId?: string,
    claimDataTypeName?: string
    userId?: string;
    firstName?: string;
    lastName?: string;
}

export type PaginationGridData = {
    rows?: GridRowModel[];
    rowsCount?: number;
    currentPage?: number;
    rowsPerPage?: number;
    sortModel?: GridSortModel;
    paginationMode?: PaginationMode;
}

interface PalmDataGridState {
    rows?: GridRowModel[];
    paginationGridData?: PaginationGridData;
    paginationModel: GridPaginationModel;
    pageInfo?: PageInfo;
}

type StateWithPalmDataGrid = { palmDataGrid: PalmDataGridState };

const initialState: PalmDataGridState = {
    paginationModel: {
        page: 0,
        pageSize: 10
    }
};

const palmDataGridSlice = createSlice({
    name: 'palmDataGrid',
    initialState,

    reducers: (create) => ({
        setRows: create.reducer((state, action: PayloadAction<GridRowModel[]>) => {
            state.rows = action.payload;
        }),

        setPageInfo: create.reducer((state, action: PayloadAction<PageInfo | undefined>) => {
            state.pageInfo = action.payload;
        }),

        setPaginationGridData: create.reducer((state, action: PayloadAction<PaginationGridData>) => {
            state.paginationGridData = {
                ...action.payload,
                sortModel: action.payload.sortModel ? [...action.payload.sortModel] : undefined
            };
        }),

        setPaginationModel: create.reducer((state, action: PayloadAction<GridPaginationModel>) => {
            state.paginationModel = action.payload;
        })
    })
});

const filterGridRowsByKeywords = (keywords: string[], rowData: GridRowsProp, columnDefinitions: ColumnDefinition[]) => {
    let filteredArray: GridRowsProp = rowData;

    for (const element of keywords) {
        const loweredElement = element.toLowerCase();
        filteredArray = filteredArray.filter((row: GridRowModel) => {
            return columnDefinitions.some((prop) => {
                const rowValue = row[prop.columnId]?.toString().toLowerCase() || '';
                return prop.shouldFilterByExactMatch
                    ? rowValue === loweredElement
                    : rowValue.includes(loweredElement);
            });
        });
    }

    return filteredArray;
};

// eslint-disable-next-line no-restricted-syntax
export default palmDataGridSlice.reducer;
export const { setRows, setPageInfo, setPaginationGridData, setPaginationModel } = palmDataGridSlice.actions;
export const selectPageInfo = (state: StateWithPalmDataGrid) => state.palmDataGrid.pageInfo;
export const selectRows = (state: StateWithPalmDataGrid) => state.palmDataGrid.rows;
export const selectPaginationGridData = (state: StateWithPalmDataGrid) => state.palmDataGrid.paginationGridData;
export const selectPaginationMode = (state: StateWithPalmDataGrid) => state.palmDataGrid.paginationGridData?.paginationMode;
export const selectPaginationModel = (state: StateWithPalmDataGrid) => state.palmDataGrid.paginationModel;
const selectFilteredRowsBase = createSelector(
    [
        selectRows,
        (_state: StateWithPalmDataGrid, columnDefinitions?: ColumnDefinition[]) => columnDefinitions,
        (_state: StateWithPalmDataGrid, _columnDefinitions?: ColumnDefinition[], filters?: FilterFields) => filters
    ],
    (rows, columnDefinitions, filters): GridRowsProp => {
        let filteredArray: GridRowsProp = rows ?? [];
        if (!filters || !columnDefinitions) {
            return filteredArray;
        }

        const { keywords, dropdowns } = filters;

        filteredArray = filterGridRowsByKeywords(keywords, filteredArray, columnDefinitions);

        if (!dropdowns) {
            return filteredArray;
        }

        const filterProps = Object.entries(dropdowns);

        for (const [key, values] of filterProps) {
            if(values.keys.length === 0) {
                continue;
            }
            if (values.asKeyword)
            {
                filteredArray = filteredArray.filter(rowData => rowData[key]?.includes(values.keys) || rowData[key]?.includes(values.keys.join(', ')));
            }
            else {
                filteredArray = filteredArray.filter(rowData => values.keys.includes(rowData[key]));
            }
        }
        return filteredArray;
    }
);

export const selectFilteredRows = (columnDefinitions?: ColumnDefinition[], filters?: FilterFields) =>
    (state: StateWithPalmDataGrid): GridRowsProp => selectFilteredRowsBase(state, columnDefinitions, filters);
